import React, { useState } from 'react';
import HolidayScene from '../../../Elements/HolidayLoaders/HolidayScene';
import { NewYearsEveScene } from '../../../Elements/HolidayLoaders/NewYearsEve';
import {
  independenceScene,
  thanksgivingScene,
  belgianScene,
  carnivalScene,
  brazilScene,
  fiestasPatriasScene,
  diaDeMuertosScene,
  mexIndependenceScene,
  chineseNewYearScene,
  chinaNationalScene,
  hungaryRevolutionScene,
  hungaryFoundationScene,
  diwaliScene,
  indiaIndependenceScene,
  singaporeScene,
  eidAlFitrScene,
  eidAlAdhaScene,
  spainNationalScene,
  epiphanyScene,
  iwdScene,
  earthDayScene,
  christmasScene,
  chileChristmasScene,
  valentineScene,
} from '../../../Elements/HolidayLoaders/handoffScenes';
import { coffeeScene } from '../../../Elements/HolidayLoaders/sceneCoffee';
import { halloweenScene } from '../../../Elements/HolidayLoaders/sceneHalloween';
import { starWarsScene } from '../../../Elements/HolidayLoaders/sceneStarWars';
import { lotrScene } from '../../../Elements/HolidayLoaders/sceneLotr';

const LOGO = '/img/logoset/ICON_NAVY_RED.svg';

// Renderer for a ported handoff scene builder.
const sc = (builder) => (key) => <HolidayScene key={key} builder={builder} />;

// The 24 handoff scenes grouped by office, plus our hand-built siblings (New
// Year's Eve, Coffee, Halloween, Star Wars, Tolkien — not in the handoff).
// Object rows (consistent shape) keep per-field types clean in .tsx — a mixed
// tuple would widen to string|function and break JSX.
const CATALOG = [
  { group: 'BARE US', label: 'Independence Day (Jul 4)', term: 'US Independence Day', render: sc(independenceScene) },
  { group: 'BARE US', label: 'Thanksgiving (4th Thu Nov)', term: 'Thanksgiving', render: sc(thanksgivingScene) },
  { group: 'BARE Belgium', label: 'Belgian National Day (Jul 21)', term: 'Belgian National Day', render: sc(belgianScene) },
  { group: 'BARE Brazil', label: 'Carnival (Feb/Mar)', term: 'Rio Carnival', render: sc(carnivalScene) },
  { group: 'BARE Brazil', label: 'Independence Day (Sep 7)', term: 'Brazil Independence Day', render: sc(brazilScene) },
  { group: 'BARE Chile', label: 'Fiestas Patrias (Sep 18–19)', term: 'Fiestas Patrias Chile', render: sc(fiestasPatriasScene) },
  { group: 'BARE Mexico', label: 'Día de Muertos (Nov 1–2)', term: 'Day of the Dead', render: sc(diaDeMuertosScene) },
  { group: 'BARE Mexico', label: 'Independence Day · El Grito (Sep 15–16)', term: 'Mexican Independence Day', render: sc(mexIndependenceScene) },
  { group: 'BARE China', label: 'Spring Festival (Jan/Feb)', term: 'Chinese New Year', render: sc(chineseNewYearScene) },
  { group: 'BARE China', label: 'National Day · Golden Week (Oct 1)', term: 'China National Day', render: sc(chinaNationalScene) },
  { group: 'BARE Hungary', label: '1848 Revolution Day (Mar 15)', term: 'Hungary 1848 Revolution', render: sc(hungaryRevolutionScene) },
  { group: 'BARE Hungary', label: "St. Stephen's Day (Aug 20)", term: "St. Stephen's Day Hungary", render: sc(hungaryFoundationScene) },
  { group: 'BARE India', label: 'Diwali (Oct/Nov)', term: 'Diwali', render: sc(diwaliScene) },
  { group: 'BARE India', label: 'Independence Day (Aug 15)', term: 'India Independence Day', render: sc(indiaIndependenceScene) },
  { group: 'BARE Singapore', label: 'National Day (Aug 9)', term: 'Singapore National Day', render: sc(singaporeScene) },
  { group: 'BARE Dubai', label: 'Eid al-Fitr (movable)', term: 'Eid al-Fitr', render: sc(eidAlFitrScene) },
  { group: 'BARE Dubai', label: 'Eid al-Adha (movable)', term: 'Eid al-Adha', render: sc(eidAlAdhaScene) },
  { group: 'BARE Spain', label: 'Fiesta Nacional (Oct 12)', term: 'Spain National Day', render: sc(spainNationalScene) },
  { group: 'BARE Spain', label: 'Epiphany · Día de Reyes (Jan 6)', term: 'Epiphany Three Kings', render: sc(epiphanyScene) },
  { group: 'General', label: "International Women's Day (Mar 8)", term: "International Women's Day", render: sc(iwdScene) },
  { group: 'General', label: 'Earth Day (Apr 22)', term: 'Earth Day', render: sc(earthDayScene) },
  { group: 'General', label: 'Christmas Day (Dec 25)', term: 'Christmas', render: sc(christmasScene) },
  { group: 'General', label: 'Christmas · Southern hemisphere (Dec 25)', term: 'Christmas', render: sc(chileChristmasScene) },
  { group: 'General', label: 'International Coffee Day (Oct 1)', term: 'International Coffee Day', render: sc(coffeeScene) },
  { group: 'Seasonal', label: "Valentine's Day (Feb 14)", term: "Valentine's Day", render: sc(valentineScene) },
  { group: 'Seasonal', label: "New Year's Eve (Dec 31)", term: "New Year's Eve", render: (key) => <NewYearsEveScene key={key} /> },
  { group: 'Seasonal', label: 'Halloween (Oct 31)', term: 'Halloween', render: sc(halloweenScene) },
  { group: 'Seasonal', label: 'Star Wars Day (May 4)', term: 'Star Wars Day', render: sc(starWarsScene) },
  { group: 'Seasonal', label: "Lord of the Rings · Tolkien's Birthday (Jan 3)", term: 'The Lord of the Rings', render: sc(lotrScene) },
];

// Composites a scene behind the centred BARE badge + a Google-Doodle info link.
function Framed({ term, children }) {
  return (
    <div>
      <div className='hol-frame'>
        {children}
        <div className='hol-mark'>
          <img src={LOGO} alt='BARE' />
          <div className='hol-ring'></div>
          <div className='hol-ring r2'></div>
        </div>
      </div>
      {term ? (
        <a
          className='hol-link'
          href={'https://www.google.com/search?q=' + encodeURIComponent(term)}
          target='_blank'
          rel='noopener noreferrer'
          aria-label={'Learn about ' + term}
        >
          {term} <span aria-hidden='true'>↗</span>
        </a>
      ) : null}
    </div>
  );
}

function LoaderDemo({ label, term, k, onReroll, children }) {
  const [fullscreen, setFullscreen] = useState(false);
  const showFullscreen = () => {
    setFullscreen(true);
    setTimeout(() => setFullscreen(false), 3500);
  };
  return (
    <div className='dsg-row'>
      <div className='dsg-demo-label'>{label}</div>
      <div style={{ width: 440, maxWidth: '100%' }}>
        <Framed term={term}>{children(k)}</Framed>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
        <button type='button' className='btn' onClick={onReroll}>
          Reroll traits
        </button>
        <button type='button' className='btn' onClick={showFullscreen}>
          Show fullscreen
        </button>
      </div>
      {fullscreen ? (
        <div
          role='presentation'
          onClick={() => setFullscreen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(10,16,26,.72)',
            backdropFilter: 'blur(2px)',
            cursor: 'pointer',
          }}
        >
          <div style={{ width: 'min(680px, 92vw)' }}>
            <Framed term={term}>{children('fs' + k)}</Framed>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function HolidayLoadersDemoComponent() {
  const [rerolls, setRerolls] = useState({});
  const bump = (i) => setRerolls((r) => ({ ...r, [i]: (r[i] || 0) + 1 }));
  return (
    <div>
      {CATALOG.map((row, i) => {
        const group = row.group;
        const label = row.label;
        const term = row.term;
        const render = row.render;
        const showHeader = i === 0 || CATALOG[i - 1].group !== group;
        return (
          <React.Fragment key={i}>
            {showHeader ? (
              <div
                className='dsg-demo-label'
                style={{ marginTop: i === 0 ? 0 : 22, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}
              >
                {group}
              </div>
            ) : null}
            <LoaderDemo label={label} term={term} k={rerolls[i] || 0} onReroll={() => bump(i)}>
              {render}
            </LoaderDemo>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export const holidayLoadersDemo = {
  id: 'holiday-loaders',
  title: 'Holiday Loaders',
  category: 'Basics',
  importPath: 'Elements/HolidayLoaders/{handoffScenes,HolidayScene}',
  notes: [
    'Festive per-office / general loading backdrops, siblings to the unicorn easter egg.',
    'The 24 office/holiday scenes are ported VERBATIM from the Claude Design handoff (handoffScenes.js — one SVG-string builder each), rendered behind the centred BARE badge via HolidayScene (dangerouslySetInnerHTML). New Year\'s Eve is a hand-built sibling (not in the handoff).',
    'Each scene rolls its own random traits per mount — hit "Reroll traits" to remount, or "Show fullscreen" to preview the full-screen overlay.',
    'Every scene links (Google-Doodle style) to a search for the holiday. All motion is gated behind prefers-reduced-motion.',
    'Wired into BravoLoader via holidayLoaderRegistry — date-driven, honouring the loaderPref cookie set on the Profile page. Coffee / Halloween / Star Wars / Tolkien are hand-authored IP-safe siblings in the same idiom.',
  ],
  snippet: "<HolidayScene builder={spainNationalScene} />  // behind the centred BARE badge",
  render: HolidayLoadersDemoComponent,
};
